import * as React from 'react';
import { Box } from "@mui/system"
import { Button, Checkbox, Menu, TextField, Tooltip } from "@mui/material";
import Grid from "@mui/material/Grid2"
import { find_rate_in_group, moneyFormat, rate_calculation, rate_chunks, rate_visualization, specific_rate_calculation } from '../../Utils';
import HelpIcon from "@mui/icons-material/Help"
import { useTrackedLocation } from '../UserState';

export default function ReservePricePreview({ reservation_length, selectedRentalType }) {
    const [selectedLocation,] = useTrackedLocation(); // REMOVED setSelectedLocation to make linter happy
    const [costEstimateMenuAnchorElement, setCostEstimateMenuAnchorElement] = React.useState(null);
    const [costEstimateMenuItemOpen, setCostEstimateMenuItemOpen] = React.useState(false);
    const [selectedOptionalFees, setSelectedOptionalFees] = React.useState(new Set());
    const [selectedQuantity, setSelectedQuantity] = React.useState(1);

    const openCostEstimateMenu = (event) => {
        setCostEstimateMenuAnchorElement(event.currentTarget)
        setCostEstimateMenuItemOpen(true)
    }

    const rate_group = find_rate_in_group(selectedRentalType.rental_rate_group.rental_rates, selectedQuantity)
    const calculated_rate = specific_rate_calculation(rate_group.rental_rates, reservation_length) * selectedQuantity
    const calculated_rate_visualization = rate_visualization(rate_chunks(rate_calculation(rate_group.rental_rates)[0]), 1000, reservation_length)
    const calculated_fees = selectedRentalType.fees.map((fee) => {
        if (fee.optional !== true || selectedOptionalFees.has(fee.id)) {
            if (fee.fee_type === 'PERCENTAGE') {
                return (calculated_rate * (fee.calculation / 100))
            } else if (fee.fee_type === 'FLAT_RATE') {
                return fee.calculation
            } else if (fee.fee_type === 'FLAT_RATE_PER_ITEM') {
                return fee.calculation * selectedQuantity
            } else {
                return 0
            }
        } else {
            return 0
        }
    }).reduce((a, b) => a + b, 0)
    const calculated_pretax_total = calculated_rate + calculated_fees
    const calculated_tax = selectedLocation.id !== "all" ? ((selectedLocation.tax_rate || 0) / 100) * calculated_pretax_total : 0
    const calculated_posttax_total = calculated_pretax_total + calculated_tax
    var calculated_deposit = 0
    // Calculate deposit
    if (selectedRentalType.deposit_type === 'FLAT_RATE') {
        calculated_deposit = selectedRentalType.deposit_calculation
    } else if (selectedRentalType.deposit_type === 'FLAT_RATE_PER_ITEM') {
        calculated_deposit = selectedRentalType.deposit_calculation * selectedQuantity
    } else if (selectedRentalType.deposit_type === 'PERCENTAGE') {
        if (selectedRentalType.deposit_include_fees) {
            calculated_deposit = (selectedRentalType.deposit_calculation / 100) * calculated_posttax_total
        } else {
            calculated_deposit = (selectedRentalType.deposit_calculation / 100) * calculated_rate
        }
    } else {
        calculated_deposit = 0
    }

    return (
        (<Box>
            <Button variant="contained" onClick={openCostEstimateMenu} style={{ minWidth: 0 }}>
                $ Estimate
            </Button>
            <Menu
                id={'header-cost-estimate-menu'}
                anchorEl={costEstimateMenuAnchorElement}
                open={costEstimateMenuItemOpen}
                onClose={() => setCostEstimateMenuItemOpen(false)}
                slotProps={{
                    list: {
                        'aria-labelledby': 'basic-button',
                    }
                }}
            >
                <Box key={`costestimate`} sx={{ border: 1, m: 1, p: 1 }}>
                    <p style={{ marginLeft: 16, marginTop: 0, marginBottom: 10 }}>Required:</p>
                    {selectedRentalType.bulk_type &&
                        <Box sx={{ ml: '32px' }}>
                            <TextField
                                key={`${selectedQuantity}`}
                                sx={{ mt: 0 }}
                                autoFocus
                                margin="dense"
                                id="quantity"
                                label="Quantity"
                                type="number"
                                fullWidth
                                variant="standard"
                                value={selectedQuantity}
                                onChange={(event) => setSelectedQuantity(Math.max(1, parseInt(event.target.value || 1)))}
                                slotProps={{
                                    htmlInput: { min: 1 }
                                }}
                            />
                        </Box>
                    }
                    <Grid container sx={{ pl: '32px' }}>
                        <Grid size="grow">
                            <span>Rental Rate
                                <Tooltip title={calculated_rate_visualization}>
                                    <HelpIcon sx={{ fontSize: 'inherit' }} />
                                </Tooltip>
                            </span>
                        </Grid>
                        <Grid size="auto">
                            <span>${moneyFormat(calculated_rate)}</span>
                        </Grid>
                    </Grid>
                    {selectedRentalType.fees.filter((item) => item.optional !== true).map((fee) => {
                        return (
                            (<Grid container sx={{ pl: '32px' }} key={`${fee.id}-fee`}>
                                <Grid size="grow">
                                    {fee.fee_type === 'PERCENTAGE' && <span>{fee.name} ({fee.calculation}%)</span>}
                                    {fee.fee_type === 'FLAT_RATE' && <span>{fee.name}</span>}
                                    {fee.fee_type === 'FLAT_RATE_PER_ITEM' && <span>{`${fee.name} ($${moneyFormat(fee.calculation)}/item)`}</span>}
                                </Grid>
                                <Grid size="auto">
                                    {fee.fee_type === 'PERCENTAGE' && <span>${moneyFormat(calculated_rate * (fee.calculation / 100))}</span>}
                                    {fee.fee_type === 'FLAT_RATE' && <span>${moneyFormat(fee.calculation)}</span>}
                                    {fee.fee_type === 'FLAT_RATE_PER_ITEM' && <span>${moneyFormat(fee.calculation * selectedQuantity)}</span>}
                                </Grid>
                            </Grid>)
                        );
                    })}
                    <Grid container sx={{ pl: '32px' }}>
                        <Grid size="grow">
                            <span>Taxes ({selectedLocation.id !== "all" ? selectedLocation.tax_rate || 0 : 'location?'}%)</span>
                        </Grid>
                        <Grid size="auto">
                            <span>${selectedLocation.id !== "all" ? moneyFormat(calculated_tax) : 'location?'}</span>
                        </Grid>
                    </Grid>
                    {selectedRentalType.fees.filter((item) => item.optional === true).length > 0 && <p style={{ marginLeft: 16 }}>Optional:</p>}
                    {selectedRentalType.fees.filter((item) => item.optional === true).map((fee) => {
                        return (
                            (<Grid container sx={{ pl: '32px' }} key={`${fee.id}-fee`}>
                                <Grid size="auto">
                                    <Checkbox
                                        sx={{ pt: 0, pb: 0 }}
                                        onChange={() => {
                                            if (selectedOptionalFees.has(fee.id)) {
                                                selectedOptionalFees.delete(fee.id)
                                            } else {
                                                selectedOptionalFees.add(fee.id)
                                            }
                                            setSelectedOptionalFees(new Set(selectedOptionalFees))
                                        }}
                                        checked={selectedOptionalFees.has(fee.id)}
                                    />
                                </Grid>
                                <Grid size="grow">
                                    {fee.fee_type === 'PERCENTAGE' && <span>{fee.name} ({fee.calculation}%)</span>}
                                    {fee.fee_type === 'FLAT_RATE' && <span>{fee.name}</span>}
                                    {fee.fee_type === 'FLAT_RATE_PER_ITEM' && <span>{`${fee.name} ($${moneyFormat(fee.calculation)}/item)`}</span>}
                                </Grid>
                                <Grid size="auto">
                                    {fee.fee_type === 'PERCENTAGE' && <span>${moneyFormat(calculated_rate * (fee.calculation / 100))}</span>}
                                    {fee.fee_type === 'FLAT_RATE' && <span>${moneyFormat(fee.calculation)}</span>}
                                    {fee.fee_type === 'FLAT_RATE_PER_ITEM' && <span>${moneyFormat(fee.calculation * selectedQuantity)}</span>}
                                </Grid>
                            </Grid>)
                        );
                    })}
                    <hr></hr>
                    <Grid container columnSpacing={3} justifyContent="flex-end" sx={{ textAlign: 'right', fontWeight: 'bold' }}>
                        <Grid size="grow">
                            <Grid container direction="column">
                                <Grid size="auto">
                                    <span>Total:</span>
                                </Grid>
                                <Grid size="auto">
                                    {selectedRentalType.deposit_type === 'PERCENTAGE' && <span>Deposit ({selectedRentalType.deposit_calculation}%{selectedRentalType.deposit_include_fees ? " w/fees" : ""}):</span>}
                                    {selectedRentalType.deposit_type === 'FLAT_RATE' && <span>Deposit:</span>}
                                    {selectedRentalType.deposit_type === 'FLAT_RATE_PER_ITEM' && <span>{`Deposit ($${moneyFormat(selectedRentalType.deposit_calculation)}/item):`}</span>}
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid size="auto">
                            <Grid container direction="column">
                                <Grid size="auto">
                                    <span>${selectedLocation.id !== "all" ? moneyFormat(calculated_posttax_total) : 'location?'}</span>
                                </Grid>
                                <Grid size="auto">
                                    <span>${selectedLocation.id !== "all" ? moneyFormat(calculated_deposit) : 'location?'}</span>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Box>
            </Menu>
        </Box>)
    );
}